import React, { useState } from "react";
import {
  useAddFeatureMutation,
  useUpdateFeatureMutation,
} from "../../../../hooks/useAdminProductCatalog";
import type { ProductFeature, ConstraintType } from "../../../../types/adminBilling";
import { ALL_CONSTRAINT_TYPES, CONSTRAINT_TYPE_LABELS, DEFAULT_CONSTRAINT_CONFIGS, FEATURE_CODE_PATTERN } from "./constants";
import { ConstraintConfigForm } from "./ConstraintConfigForm";

export interface FeatureFormProps {
  productId: string;
  brandId: string;
  feature?: ProductFeature | null;
  onClose: () => void;
}

export const FeatureForm: React.FC<FeatureFormProps> = ({
  productId,
  brandId,
  feature,
  onClose,
}) => {
  const isEditing = !!feature;
  const [featureCode, setFeatureCode] = useState(feature?.featureCode ?? "");
  const [constraintType, setConstraintType] = useState<ConstraintType>(
    feature?.constraintType ?? "BOOLEAN"
  );
  const [constraintConfig, setConstraintConfig] = useState<Record<string, unknown>>(
    feature?.constraintConfig ?? DEFAULT_CONSTRAINT_CONFIGS[constraintType]
  );
  const [constraintConfigError, setConstraintConfigError] = useState<
    string | null
  >(null);
  const [isMandatory, setIsMandatory] = useState(
    feature?.isMandatory ?? true
  );

  const addMutation = useAddFeatureMutation(productId, brandId);
  const updateMutation = useUpdateFeatureMutation(productId, brandId);
  const isLoading = addMutation.isPending || updateMutation.isPending;

  // L-15: Inline validation for featureCode format
  const featureCodeError =
    featureCode.trim() && !FEATURE_CODE_PATTERN.test(featureCode.trim())
      ? "Nieprawidłowy format. Dozwolone prefiksy: module, limit, scope, config (np. module:premium, limit:users)"
      : null;

  const handleConstraintTypeChange = (newType: ConstraintType) => {
    setConstraintType(newType);
    setConstraintConfig(DEFAULT_CONSTRAINT_CONFIGS[newType]);
    setConstraintConfigError(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!featureCode.trim()) return;
    if (featureCodeError) return;

    if (isEditing && feature) {
      updateMutation.mutate(
        {
          featureId: feature.id,
          dto: {
            featureCode: featureCode.trim(),
            constraintType,
            constraintConfig,
            isMandatory,
          },
        },
        { onSuccess: onClose }
      );
    } else {
      addMutation.mutate(
        {
          featureCode: featureCode.trim(),
          constraintType,
          constraintConfig,
          isMandatory,
        },
        { onSuccess: onClose }
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3 p-4 bg-neutral-50 rounded-md border border-neutral-200 mt-3">
      <h4 className="text-sm font-medium text-neutral-900">
        {isEditing ? "Edytuj feature" : "Dodaj feature"}
      </h4>
      <div>
        <label htmlFor="feature-code" className="block text-xs font-medium text-neutral-700 mb-1">
          Feature Code *
        </label>
        <input
          id="feature-code"
          type="text"
          value={featureCode}
          onChange={(e) => setFeatureCode(e.target.value)}
          required
          placeholder="np. module:premium, limit:users, scope:region"
          className={`w-full border rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 ${
            featureCodeError ? "border-red-300" : "border-neutral-300"
          }`}
        />
        {/* L-15: Show validation error or helper text */}
        {featureCodeError ? (
          <p className="text-red-600 text-xs mt-1">{featureCodeError}</p>
        ) : (
          <p className="text-neutral-500 text-xs mt-1">
            Format: prefix:nazwa. Dozwolone prefiksy: module, limit, scope, config
          </p>
        )}
      </div>
      <div>
        <label htmlFor="feature-constraint-type" className="block text-xs font-medium text-neutral-700 mb-1">
          Typ ograniczenia *
        </label>
        <select
          id="feature-constraint-type"
          value={constraintType}
          onChange={(e) => handleConstraintTypeChange(e.target.value as ConstraintType)}
          className="w-full border border-neutral-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {ALL_CONSTRAINT_TYPES.map((ct) => (
            <option key={ct} value={ct}>
              {CONSTRAINT_TYPE_LABELS[ct]}
            </option>
          ))}
        </select>
      </div>
      <ConstraintConfigForm
        constraintType={constraintType}
        config={constraintConfig}
        onChange={setConstraintConfig}
        error={constraintConfigError}
      />
      <label className="flex items-center gap-2 text-sm text-neutral-700">
        <input
          type="checkbox"
          checked={isMandatory}
          onChange={(e) => setIsMandatory(e.target.checked)}
          className="h-4 w-4 text-primary-600 border-neutral-300 rounded"
        />
        Obowiązkowy feature
      </label>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="px-3 py-1.5 text-xs text-neutral-600 hover:text-neutral-800"
        >
          Anuluj
        </button>
        <button
          type="submit"
          disabled={isLoading || !!featureCodeError}
          className="px-3 py-1.5 text-xs bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
        >
          {isLoading ? "Zapisywanie..." : "Zapisz"}
        </button>
      </div>
    </form>
  );
};
